import { useState } from "react";

const GOLD = "#0077B5";
const GOLD_DIM = "#005885";
const GOLD_DARK = "#EBF5FB";
const CARD = "#FFFFFF";
const BORDER = "#E1E9EE";

export default function FollowerGate({
  followerInput, setFollowerInput, handleSaveFollowers, followerSaved,
  isWorkspaceMember, hasPlugConfigured, onSetupPlug, onDismissPlug
}) {
  const [step, setStep] = useState(1); // 1 = follower count, 2 = evergreen pitch
  const [saving, setSaving] = useState(false);
  const [dismissed, setDismissed] = useState(false);

  // Gate 1 completed — advance to Gate 2 or close
  const handleSave = async () => {
    const n = parseInt(followerInput, 10);
    if (isNaN(n) || n < 0) return;
    setSaving(true);
    await handleSaveFollowers();
    setSaving(false);
    // If team member or plug already configured — close entirely
    if (isWorkspaceMember || hasPlugConfigured) {
      setDismissed(true);
    } else {
      setStep(2);
    }
  };

  const handleDismissPlug = () => {
    localStorage.setItem("nectar_plug_pitch_dismissed", "1");
    if (onDismissPlug) onDismissPlug();
    setDismissed(true);
  };

  const handleSetupPlug = () => {
    localStorage.setItem("nectar_plug_pitch_dismissed", "1");
    if (onDismissPlug) onDismissPlug();
    onSetupPlug();
  };

  if (dismissed) return null;

  const inputValid = followerInput && !isNaN(parseInt(followerInput, 10)) && parseInt(followerInput, 10) >= 0;

  return (
    <div style={{
      position: "fixed", top: 0, left: 0, right: 0, bottom: 0,
      background: "rgba(0,0,0,0.55)", zIndex: 9999,
      display: "flex", alignItems: "center", justifyContent: "center",
      padding: 20, fontFamily: "-apple-system,'Segoe UI',Helvetica,sans-serif"
    }}>
      <div style={{
        background: CARD, borderRadius: 16, padding: "48px 40px", maxWidth: 480, width: "100%",
        boxShadow: "0 20px 60px rgba(0,0,0,0.3)", position: "relative"
      }}>

        {/* ═══ GATE 1: Follower Count ═══ */}
        {step === 1 && (
          <div>
            <div style={{ fontSize: 28, fontFamily: "'Playfair Display',Georgia,serif", lineHeight: 1.3, marginBottom: 12, color: "#1A1A1A" }}>
              One last thing<br />before you're in.
            </div>
            <div style={{ fontSize: 14, color: "#555", lineHeight: 1.7, marginBottom: 28 }}>
              Enter your LinkedIn follower count so we can show you accurate reach estimates on your dashboard.
            </div>

            <a
              href="https://www.linkedin.com/mynetwork/network-manager/people-follow/followers/"
              target="_blank"
              rel="noopener noreferrer"
              style={{ display: "inline-block", fontSize: 13, color: GOLD, fontWeight: 600, marginBottom: 16, textDecoration: "none" }}
            >
              Check my follower count &rarr;
            </a>

            <div style={{ marginBottom: 8 }}>
              <div style={{ fontSize: 11, color: "#666", letterSpacing: "0.12em", marginBottom: 8, textTransform: "uppercase" }}>Follower count</div>
              <input
                value={followerInput}
                onChange={e => {
                  const val = e.target.value.replace(/[^0-9]/g, "");
                  setFollowerInput(val);
                }}
                placeholder="e.g. 1,250"
                type="text"
                inputMode="numeric"
                style={{
                  width: "100%", background: "#F9FAFB", border: `1px solid ${BORDER}`,
                  borderRadius: 9, padding: "14px 16px", color: "#1A1A1A",
                  fontFamily: "-apple-system,'Segoe UI',Helvetica,sans-serif", fontSize: 16
                }}
              />
            </div>

            <button
              onClick={handleSave}
              disabled={!inputValid || saving}
              style={{
                width: "100%", marginTop: 20,
                background: inputValid && !saving ? GOLD : GOLD_DARK,
                color: inputValid && !saving ? "#fff" : "#999",
                border: "none", borderRadius: 10, padding: "16px",
                fontSize: 15, fontWeight: 600, cursor: inputValid && !saving ? "pointer" : "not-allowed",
                fontFamily: "-apple-system,'Segoe UI',Helvetica,sans-serif",
                transition: "all 0.2s"
              }}
            >
              {saving ? "Saving..." : "Save & unlock dashboard \u2192"}
            </button>
          </div>
        )}

        {/* ═══ GATE 2: Evergreen Plug Pitch (regular users only) ═══ */}
        {step === 2 && (
          <div>
            <div style={{ fontSize: 28, fontFamily: "'Playfair Display',Georgia,serif", lineHeight: 1.3, marginBottom: 12, color: "#1A1A1A" }}>
              Your posts will be driving views.<br />
              <em style={{ color: GOLD }}>Now let's give them a reason to reach out.</em>
            </div>
            <div style={{ fontSize: 14, color: "#555", lineHeight: 1.7, marginBottom: 32 }}>
              Set up your weekly plug post — one post that tells your audience exactly what you do and how to reach you. It runs automatically alongside your content.
            </div>

            <button
              onClick={handleSetupPlug}
              style={{
                width: "100%", background: GOLD, color: "#fff",
                border: "none", borderRadius: 10, padding: "16px",
                fontSize: 15, fontWeight: 600, cursor: "pointer",
                fontFamily: "-apple-system,'Segoe UI',Helvetica,sans-serif",
                transition: "all 0.2s", marginBottom: 12
              }}
            >
              Set up my plug post &rarr;
            </button>

            <button
              onClick={handleDismissPlug}
              style={{
                width: "100%", background: "transparent", color: "#888",
                border: `1px solid ${BORDER}`, borderRadius: 10, padding: "14px",
                fontSize: 14, cursor: "pointer",
                fontFamily: "-apple-system,'Segoe UI',Helvetica,sans-serif"
              }}
            >
              I'll do this later
            </button>
          </div>
        )}

      </div>
    </div>
  );
}
